import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  Alert,
  AlertTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

interface FaceCaptureProps {
  open: boolean;
  onClose: () => void;
  onCaptured: (imageFile: File) => void;
  title?: string;
}

export const FaceCapture: React.FC<FaceCaptureProps> = ({
  open,
  onClose,
  onCaptured,
  title = "Captura de Face",
}) => {
  // Estados essenciais (baseados no IMEIScanner)
  const [isScanning, setIsScanning] = useState(false);
  const [captureStatus, setCaptureStatus] = useState<
    "ready" | "capturing" | "captured" | "idle"
  >("idle");
  const [isMobile, setIsMobile] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  // Refs para captura
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Ref separado para o MediaStream — sobrevive ao unmount do <video>
  const streamRef = useRef<MediaStream | null>(null);

  // Para todos os tracks da stream de câmera usando streamRef (independente do DOM)
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Desliga a câmera automaticamente quando o dialog fecha ou o componente é desmontado
  useEffect(() => {
    if (!open) {
      stopStream();
      setIsScanning(false);
      setCameraReady(false);
      setCaptureStatus("idle");
    }
    return () => {
      stopStream();
    };
  }, [open, stopStream]);

  // Detectar se é dispositivo mobile (reutilizado do IMEIScanner)
  useEffect(() => {
    const checkMobile = () => {
      const userAgent =
        navigator.userAgent || navigator.vendor || (window as any).opera;
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent.toLowerCase()
        );
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;

      setIsMobile(isMobileDevice || isTouchDevice);
    };

    checkMobile();
  }, []);

  // Função para fechar a câmera
  const closeCamera = useCallback(() => {
    stopStream();
    setIsScanning(false);
    setCaptureStatus("idle");
    setCameraReady(false);
    onClose();
  }, [onClose, stopStream]);

  // Função para capturar foto do suspeito
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    // Dimensões do quadrado de captura (área central)
    const captureSize = isMobile ? 280 : 320;

    // Configurar canvas com o tamanho do quadrado
    canvas.width = captureSize;
    canvas.height = captureSize;

    // Calcular área central do vídeo para crop quadrado
    const { videoWidth, videoHeight } = video;
    const videoElement = video.getBoundingClientRect();
    const scaleX = videoWidth / videoElement.width;
    const scaleY = videoHeight / videoElement.height;

    // Calcular posição central para crop quadrado
    const centerX = videoWidth / 2;
    const centerY = videoHeight / 2;
    const cropSize = Math.min(videoWidth, videoHeight) * 0.7; // 70% da menor dimensão
    const cropX = centerX - cropSize / 2;
    const cropY = centerY - cropSize / 2;

    // Desenhar a área cropada no canvas
    ctx.drawImage(
      video,
      cropX,
      cropY,
      cropSize,
      cropSize,
      0,
      0,
      captureSize,
      captureSize
    );

    setCaptureStatus("captured");

    // Converter canvas para blob e criar arquivo
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
          const fileName = `face-capture-${timestamp}.jpg`;
          const file = new File([blob], fileName, { type: "image/jpeg" });

          onCaptured(file);
          closeCamera();
        }
      },
      "image/jpeg",
      0.9
    );
  }, [isMobile, onCaptured, closeCamera]);

  // Função para iniciar a câmera (baseada no IMEIScanner)
  const startCamera = useCallback(async () => {
    setIsScanning(true);
    setCameraReady(false);
    setCaptureStatus("ready");

    try {
      // Verificar se mediaDevices é suportado
      if (!navigator.mediaDevices) {
        throw new Error(
          "Acesso à câmera não suportado neste navegador ou ambiente (HTTPS necessário)"
        );
      }

      // Verificar permissões
      const permissionStatus = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });

      if (permissionStatus.state === "denied") {
        throw new Error(
          "Permissão de câmera negada. Verifique as configurações do seu navegador."
        );
      }

      if (!videoRef.current) {
        throw new Error("Elemento de vídeo não encontrado");
      }

      // Configurações de câmera otimizadas para captura de suspeito
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Câmera traseira sempre (policial fotografando suspeito)
          width: isMobile
            ? { ideal: 640, max: 1280 }
            : { ideal: 1920, min: 1280 },
          height: isMobile
            ? { ideal: 480, max: 720 }
            : { ideal: 1080, min: 720 },
          frameRate: { ideal: 30, min: 15 },
          ...(isMobile
            ? {}
            : {
                focusMode: "continuous",
                exposureMode: "continuous",
                whiteBalanceMode: "continuous",
              }),
        } as MediaTrackConstraints,
      });

      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      videoRef.current.play();
      setCameraReady(true);
    } catch (err) {
      console.error("Erro ao acessar câmera:", err);
      alert(
        `Erro ao acessar a câmera: ${
          err instanceof Error ? err.message : "Desconhecido"
        }`
      );
      onClose();
    }
  }, [isMobile, onClose]);

  // Iniciar câmera automaticamente quando modal abrir (apenas desktop)
  useEffect(() => {
    if (open && !isMobile) {
      const timer = setTimeout(() => {
        startCamera();
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }

    if (open && isMobile) {
      setCameraReady(false);
    }

    return undefined;
  }, [open, startCamera, isMobile]);

  // Componente de Overlay para captura
  const CaptureOverlay = () => {
    const captureSize = isMobile ? 280 : 320;

    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
        }}
      >
        {/* Área escura ao redor */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          }}
        />

        {/* Quadrado de captura */}
        <Box
          sx={{
            position: "absolute",
            width: `${captureSize}px`,
            height: `${captureSize}px`,
            border: `4px solid ${
              captureStatus === "captured" ? "#4caf50" : "#2196f3"
            }`,
            borderRadius: "12px",
            backgroundColor: "transparent",
            zIndex: 1,
            animation: captureStatus === "ready" ? "pulse 2s infinite" : "none",

            // Cantos estilizados
            "&::before, &::after": {
              content: '""',
              position: "absolute",
              width: "30px",
              height: "30px",
              border: `4px solid #fff`,
            },
            "&::before": {
              top: "-4px",
              left: "-4px",
              borderRight: "none",
              borderBottom: "none",
            },
            "&::after": {
              bottom: "-4px",
              right: "-4px",
              borderLeft: "none",
              borderTop: "none",
            },

            "@keyframes pulse": {
              "0%": { opacity: 1, transform: "scale(1)" },
              "50%": { opacity: 0.8, transform: "scale(1.02)" },
              "100%": { opacity: 1, transform: "scale(1)" },
            },
          }}
        >
          {/* Texto de instrução */}
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: "-35px",
              left: "50%",
              transform: "translateX(-50%)",
              color: "#fff",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              padding: "6px 12px",
              borderRadius: "6px",
              whiteSpace: "nowrap",
              fontWeight: "bold",
            }}
          >
            {(() => {
              if (captureStatus === "captured") return "✓ Foto Capturada!";
              if (captureStatus === "ready") return "📷 Enquadre o rosto aqui";
              return "👤 Posicionando câmera...";
            })()}
          </Typography>

          {/* Grid de auxílio */}
          <Box
            sx={{
              position: "absolute",
              top: "33%",
              left: 0,
              right: 0,
              height: "1px",
              backgroundColor: "rgba(255, 255, 255, 0.3)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "66%",
              left: 0,
              right: 0,
              height: "1px",
              backgroundColor: "rgba(255, 255, 255, 0.3)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "33%",
              width: "1px",
              backgroundColor: "rgba(255, 255, 255, 0.3)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "66%",
              width: "1px",
              backgroundColor: "rgba(255, 255, 255, 0.3)",
            }}
          />
        </Box>
      </Box>
    );
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={closeCamera}
      maxWidth={isMobile ? false : "sm"}
      fullWidth
      fullScreen={isMobile}
      sx={{
        "& .MuiDialog-paper": {
          ...(isMobile && {
            margin: 0,
            width: "100%",
            height: "100%",
            maxHeight: "none",
          }),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: isMobile ? "none" : "block",
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={closeCamera}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Botão de fechar sobreposto no mobile */}
      {isMobile && (
        <IconButton
          aria-label="close"
          onClick={closeCamera}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 1000,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            color: "#fff",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <DialogContent
        sx={{
          p: isMobile ? 0 : 2,
          height: isMobile ? "100vh" : "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: isMobile ? "100%" : "400px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: isMobile ? "100%" : "300px",
              borderRadius: isMobile ? 0 : "8px",
              overflow: "hidden",
            }}
          >
            <video
              ref={videoRef}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              playsInline
              muted
            >
              <track kind="captions" />
            </video>

            {/* Canvas oculto para captura */}
            <canvas
              ref={canvasRef}
              style={{
                display: "none",
              }}
            />

            {cameraReady && <CaptureOverlay />}
          </Box>

          {/* Botão de captura no mobile */}
          {isMobile && cameraReady && (
            <Box
              sx={{
                position: "absolute",
                bottom: "10%",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
              }}
            >
              <IconButton
                onClick={capturePhoto}
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: "#2196f3",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#1976d2",
                  },
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 40 }} />
              </IconButton>
            </Box>
          )}

          {/* Estados da câmera no mobile */}
          {isMobile && !isScanning && !cameraReady && (
            <Box
              sx={{
                position: "absolute",
                bottom: "20%",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10,
                width: "90%",
                maxWidth: "400px",
              }}
            >
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>📷 Captura de Face</AlertTitle>
                Posicione o rosto do suspeito dentro do quadrado azul.
                <br />
                Mantenha boa iluminação para melhor qualidade da foto.
              </Alert>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={startCamera}
                startIcon={<CameraAltIcon />}
              >
                Iniciar Câmera
              </Button>
            </Box>
          )}

          {!isMobile && !isScanning && !cameraReady && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Aguarde</AlertTitle>
              Inicializando câmera automaticamente...
            </Alert>
          )}

          {isScanning && !cameraReady && (
            <Box
              sx={{
                position: isMobile ? "absolute" : "static",
                bottom: isMobile ? "30%" : "auto",
                left: isMobile ? "50%" : "auto",
                transform: isMobile ? "translateX(-50%)" : "none",
                zIndex: 10,
                width: isMobile ? "90%" : "auto",
              }}
            >
              <Alert severity="info">
                <AlertTitle>Aguarde</AlertTitle>
                Solicitando acesso à câmera...
              </Alert>
            </Box>
          )}

          {/* Instruções quando câmera está ativa */}
          {cameraReady && isScanning && !isMobile && (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                📷 Posicione o rosto dentro do quadrado azul
                <br />
                💡 Mantenha boa iluminação para melhor qualidade
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Botão de captura no desktop */}
      {!isMobile && cameraReady && (
        <Box sx={{ p: 2, display: "flex", justifyContent: "center", gap: 2 }}>
          <Button onClick={closeCamera} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={capturePhoto}
            variant="contained"
            color="primary"
            startIcon={<PhotoCameraIcon />}
          >
            Capturar Foto
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default FaceCapture;
